/**
 * <MessageComposer> — écrire et envoyer un message (molecule).
 *
 * Une carte : le bouton des pièces jointes, le champ qui grandit avec le texte, le bouton
 * d'envoi. Le même composeur sert la conversation avec Sam (texte seul) et la messagerie avec un
 * tuteur (photo, image, fichier, message vocal) : les sources sont une liste, et sans liste il
 * n'y a pas de bouton.
 *
 * Contrôlé : le brouillon (`value`), l'envoi en cours (`sending`), la pièce qui part
 * (`attaching`) et la raison d'un composeur fermé (`disabledReason`) arrivent en props. Seuls la
 * hauteur du champ et le menu des sources vivent ici.
 *
 * Fermé, il DIT pourquoi, dans la carte, à la place du champ (« Sam revient demain ») : l'ancien
 * code l'écrivait en texte indicatif d'un champ grisé, à moitié lisible.
 *
 * Une pièce choisie part aussitôt, sans aperçu : c'est ce que fait la messagerie de l'élève
 * aujourd'hui. Le composeur remonte la source (`onAttach`) ; le sélecteur, l'appareil photo et
 * l'enregistreur sont des effets de l'écran. Ce n'est donc pas `<MediaPicker>`, qui garde ses
 * aperçus jusqu'à l'envoi d'un formulaire.
 *
 * **Une barre de composition** (`toolbar`, le live du masteur) : le texte en haut, puis, DANS le même cadre, le
 * trombone, la rangée d'options de l'appelant et l'envoi à droite — les réglages font partie du message, pas
 * d'une rangée posée à côté. `header` pose au-dessus du texte ce qui part avec lui (une pièce jointe). Sans
 * `toolbar` (défaut), la ligne d'aujourd'hui : trombone, champ, envoi.
 *
 * Le champ n'est pas `<TextInput>` : celui-ci est un champ de formulaire (intitulé, 100 px de
 * haut en multiligne, pas de touche Entrée). Ici une ligne suffit au repos, et sur le web Entrée
 * envoie, Maj+Entrée va à la ligne — comme dans les deux anciens fils.
 */
import React, { useLayoutEffect, useRef, useState } from "react"
import {
    Platform,
    TextInput as RNTextInput,
    View,
    type NativeSyntheticEvent,
    type TextInputContentSizeChangeEventData,
    type TextInputKeyPressEventData,
} from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { textLineBox } from "../../atoms/Skeleton/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { ActionSheet } from "../ActionSheet"
import { Card } from "../Card"

/** D'où vient une pièce jointe. Le fil de l'élève en a quatre aujourd'hui. */
export type ComposerAttachSource = "camera" | "gallery" | "document" | "voice"

export type MessageComposerProps = {
    value: string
    onChangeText: (text: string) => void
    /** Envoyer le brouillon. N'est jamais appelé sur un brouillon vide, sauf `ready`. */
    onSend: () => void
    /** « Écrire un message... » par défaut. */
    placeholder?: string
    /** La borne du serveur — Sam refuse au-delà de 2 000 caractères. */
    maxLength?: number
    /** L'envoi est en cours : le bouton tourne et ne répond plus ; on peut continuer d'écrire. */
    sending?: boolean
    /** Fermé, et pourquoi : la phrase prend la place du champ. */
    disabledReason?: string
    /** Les sources offertes, dans l'ordre. Absent ou vide : pas de bouton de pièce jointe. */
    attachSources?: ComposerAttachSource[]
    onAttach?: (source: ComposerAttachSource) => void
    /** Le glyphe du bouton des pièces jointes. Défaut `plus` ; le trombone quand le champ joint un exercice. */
    attachIcon?: IconName
    /**
     * Prêt à partir même sans texte (une configuration suffit). Défaut `false` : le texte seul fait partir.
     */
    ready?: boolean
    /** Le glyphe du bouton d'envoi. Défaut `telegram-plane` ; « Entrée » (`level-down-alt`) au live du masteur. */
    sendIcon?: IconName
    /**
     * Le bouton d'envoi n'apparaît que lorsqu'il y a quelque chose à envoyer (Wajih, le live du masteur).
     * Défaut `false` : toujours là, grisé à vide.
     */
    sendOnlyWhenReady?: boolean
    /** La rangée d'options de la barre de composition, sous le texte. Absente (défaut) : une seule ligne. */
    toolbar?: React.ReactNode
    /** Ce qui part avec le texte, posé au-dessus de lui dans le cadre (une pièce jointe). Absent : rien. */
    header?: React.ReactNode
    /** Le champ prend ou perd le focus (une barre qui se déplie au focus). Absent : rien n'est dit. */
    onFocusChange?: (focused: boolean) => void
    /** Une pièce part : son bouton tourne et ne répond plus. */
    attaching?: boolean
    autoFocus?: boolean
    /**
     * La ref du champ, quand l'écran doit l'écouter lui-même (au web, le collage et le dépôt d'un fichier
     * sur ce champ seulement, jamais sur la page). Absente (défaut) : le composant garde la sienne.
     */
    inputRef?: React.MutableRefObject<RNTextInput | null>
    testID?: string
}

const SOURCES: Record<ComposerAttachSource, { icon: IconName; label: string }> = {
    camera: { icon: "camera", label: "Prendre une photo" },
    gallery: { icon: "file-image", label: "Ouvrir la galerie" },
    document: { icon: "file", label: "Choisir un document (pdf, image)" },
    voice: { icon: "microphone", label: "Message vocal" },
}

const TEXTS = {
    placeholder: "Écrire un message...",
    send: "Envoyer",
    attach: "Joindre une photo, un fichier ou un message vocal",
}

/** Les deux boutons : la cible d'un doigt. */
/**
 * Sur le web, un appui dans la barre (une option, le trombone, l'envoi) ne retire pas le focus au champ : le
 * navigateur le déplace au `mousedown`, avant que le geste n'arrive. React Native n'en a pas besoin : toucher
 * un bouton n'y quitte pas le champ.
 */
const keepFieldFocus =
    Platform.OS === "web"
        ? ({ onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault() } as object)
        : {}

const ACTION_SIZE = 44
/** Les boutons de la rangée d'options : la hauteur d'une chip et un peu d'air, pour une rangée égale. */
const TOOL_SIZE = 36
const FIELD_TEXT = "text-lg"
const FIELD_PADDING_V = "3"
/** Le champ grandit jusqu'à cinq lignes, puis défile. */
const MAX_LINES = 5

export function MessageComposer(props: MessageComposerProps) {
    const {
        value,
        onChangeText,
        onSend,
        placeholder = TEXTS.placeholder,
        maxLength,
        sending = false,
        disabledReason,
        attachSources = [],
        onAttach,
        attachIcon = "plus",
        ready = false,
        sendIcon = "telegram-plane",
        sendOnlyWhenReady = false,
        attaching = false,
        toolbar,
        header,
        onFocusChange,
        autoFocus,
        inputRef,
        testID,
    } = props

    const { theme } = useUnistyles()
    const ownInput = useRef<RNTextInput>(null)
    const input = inputRef ?? ownInput
    const [menuOpen, setMenuOpen] = useState(false)
    const [contentHeight, setContentHeight] = useState(0)

    const closed = disabledReason != null && disabledReason !== ""
    const canSend = (value.trim() !== "" || ready) && !closed && !sending

    const send = () => {
        if (!canSend) return
        onSend()
        // Le champ garde la main : l'élève répond tour après tour sans le viser à nouveau.
        input.current?.focus()
    }

    // Sur ordinateur, Entrée envoie et Maj+Entrée va à la ligne. Une saisie en cours de
    // composition (accents, IME) n'envoie pas.
    const onKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        if (Platform.OS !== "web") return
        const key = event.nativeEvent as TextInputKeyPressEventData & { shiftKey?: boolean; isComposing?: boolean }
        if (key.key !== "Enter" || key.shiftKey === true || key.isComposing === true) return
        event.preventDefault()
        send()
    }

    const line = textLineBox(FIELD_TEXT)
    const padding = 2 * theme.spacing[FIELD_PADDING_V]

    // Sur mobile, le champ dit la hauteur de son texte à chaque changement.
    const onContentSizeChange = (event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
        if (Platform.OS !== "web") setContentHeight(event.nativeEvent.contentSize.height + padding)
    }

    // Sur le web, un `<textarea>` à hauteur fixe ne rapporte jamais moins que sa propre hauteur :
    // il grandirait sans jamais revenir. On le mesure donc replié, à chaque changement du brouillon.
    useLayoutEffect(() => {
        if (Platform.OS !== "web") return
        const node = input.current as unknown as HTMLTextAreaElement | null
        if (node == null) return
        const height = node.style.height
        node.style.height = "0px"
        const measured = node.scrollHeight
        node.style.height = height
        setContentHeight(measured)
    }, [value, closed, input])

    const fieldHeight = Math.min(Math.max(contentHeight, line + padding), MAX_LINES * line + padding)

    const attachButton = attachSources.length > 0 && (
        <IconButton
            iconName={attachIcon}
            appearance="transparent"
            iconColor="text-primary"
            size={toolbar != null ? TOOL_SIZE : ACTION_SIZE}
            onPress={() => setMenuOpen(true)}
            disabled={closed}
            loading={attaching}
            accessibilityLabel={TEXTS.attach}
        />
    )
    const sendButton = (!sendOnlyWhenReady || canSend || sending) && (
        <IconButton
            iconName={sendIcon}
            size={toolbar != null ? TOOL_SIZE : ACTION_SIZE}
            color="role-fill"
            onPress={send}
            disabled={!canSend && !sending}
            loading={sending}
            accessibilityLabel={TEXTS.send}
        />
    )
    const field = closed ? (
        <Row gap="2" align="center" style={styles.reason(toolbar == null && attachSources.length > 0)}>
            <Icon name="hourglass-half" size={16} color="text-muted" />
            <Text size="text-md" color="text-muted" style={styles.shrink}>
                {disabledReason}
            </Text>
        </Row>
    ) : (
        <View style={styles.fieldBox(toolbar == null && attachSources.length > 0)}>
            <RNTextInput
                ref={input}
                style={styles.field(fieldHeight)}
                value={value}
                onChangeText={onChangeText}
                placeholder={placeholder}
                placeholderTextColor={theme.colors["text-muted"]}
                multiline
                maxLength={maxLength}
                autoFocus={autoFocus}
                // Le clavier reste ouvert d'un tour à l'autre.
                blurOnSubmit={false}
                onKeyPress={onKeyPress}
                onContentSizeChange={onContentSizeChange}
                onFocus={onFocusChange != null ? () => onFocusChange(true) : undefined}
                onBlur={onFocusChange != null ? () => onFocusChange(false) : undefined}
                accessibilityLabel={placeholder}
            />
        </View>
    )

    // Le champ garde sa place quelle que soit la mise en page : la barre qui se déplie au focus ne le
    // remonte pas (il perdrait le curseur au premier clic).
    return (
        <Card p="1" testID={testID}>
            <View style={toolbar != null ? styles.bar : undefined}>
                {header != null && <View {...keepFieldFocus}>{header}</View>}
                <Row gap="1" align="end">
                    {toolbar == null && attachButton}
                    {field}
                    {toolbar == null && sendButton}
                </Row>
                {toolbar != null && (
                    <View {...keepFieldFocus}>
                        <Row gap="1" align="center">
                            {attachButton}
                            <View style={styles.toolbar}>{toolbar}</View>
                            {sendButton}
                        </Row>
                    </View>
                )}
            </View>

            <ActionSheet
                visible={menuOpen}
                onClose={() => setMenuOpen(false)}
                items={attachSources.map((source) => ({
                    icon: SOURCES[source].icon,
                    label: SOURCES[source].label,
                    onPress: () => onAttach?.(source),
                }))}
            />
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    fieldBox: (afterButton: boolean) => ({
        flex: 1,
        paddingLeft: afterButton ? 0 : theme.spacing["3"],
        paddingRight: theme.spacing["1"],
    }),
    field: (height: number) => ({
        height,
        paddingVertical: theme.spacing[FIELD_PADDING_V],
        fontFamily: theme.fontFamilies.regular,
        fontSize: theme.fontSizes[FIELD_TEXT],
        color: theme.colors["text-strong"],
        textAlignVertical: "top" as const,
        // Le navigateur dessine son anneau de focus ; la carte porte déjà le champ.
        ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : null),
    }),
    reason: (afterButton: boolean) => ({
        flex: 1,
        minHeight: ACTION_SIZE,
        paddingLeft: afterButton ? 0 : theme.spacing["3"],
        paddingRight: theme.spacing["1"],
    }),
    shrink: { flex: 1, flexShrink: 1 },
    /** La barre de composition : le texte au-dessus, la rangée d'options dessous, un seul cadre. */
    bar: { gap: theme.spacing["1"] },
    toolbar: { flex: 1, minWidth: 0 },
}))
